import React, { useState, useEffect, useCallback } from "react";
import {
  Space,
  Row,
  Col,
  Spin,
  Table,
  Tag,
  Typography,
  Tooltip,
  Empty,
} from "antd";
import type { TableColumnsType } from "antd";
import { ValidationService } from "../../../services/ValidationService";
import { usePermissions } from "../../../PermissionsProvider";
import {
  ValidationRequest,
  ValidationRun,
} from "../../../model_gen/finetuning";
import {
  FileTextOutlined,
  RocketOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import RequestViewModal from "../../Request/RequestViewModal";
import { RequestViewButton } from "../../shared/button/Buttons";

const { Text } = Typography;

interface ValidationTabsProps {
  validationRunId?: string;
  finetuneRunId?: string;
}

const ValidationDetails: React.FC<ValidationTabsProps> = ({
  validationRunId,
  finetuneRunId,
}) => {
  const { apiKey } = usePermissions();
  const [validationRun, setValidationRun] = useState<ValidationRun>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [resolvedValidationRunId, setResolvedValidationRunId] = useState<
    string | undefined
  >(validationRunId);
  const [selectedRequest, setSelectedRequest] =
    useState<ValidationRequest | null>(null);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);

  const styles = {
    monospaceFont: {
      fontFamily: "monospace",
      fontSize: "13px",
    },
    statsCard: (color: string) => ({
      background: `linear-gradient(135deg, ${color}10 0%, #f8f8f8 100%)`,
      padding: "14px 16px",
      borderRadius: "12px",
      height: "100%",
      border: `1px solid ${color}30`,
      boxShadow: "0 2px 6px rgba(0, 0, 0, 0.03)",
      transition: "all 0.3s ease",
      display: "flex",
      alignItems: "center",
    }),
    tableContainer: {
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
    },
    tagStyle: {
      padding: "4px 10px",
      borderRadius: "4px",
      fontSize: "12px",
    },
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case "COMPLETED":
        return "success";
      case "FAILED":
        return "error";
      case "RUNNING":
        return "processing";
      default:
        return "default";
    }
  };

  // Fetch the latest validation run for the finetune run if validationRunId is not provided
  const fetchLatestValidationRun = useCallback(() => {
    if (!finetuneRunId || validationRunId) return;
    setIsLoading(true);
    ValidationService.getValidationRunsByFinetuneRunId(
      finetuneRunId,
      null,
      apiKey
    ).subscribe(
      (data: any) => {
        if (data && Array.isArray(data) && data.length > 0) {
          // Sort by date_created (descending) to get the most recent run
          const sortedRuns = [...data].sort(
            (a, b) => (b.date_created || 0) - (a.date_created || 0)
          );
          const latestRunId = sortedRuns[0]?.id;
          if (latestRunId) {
            setResolvedValidationRunId(latestRunId);
          }
        } else {
          setIsLoading(false);
          console.log("No validation runs found for this finetune run");
        }
      },
      (error: any) => {
        console.error("Error fetching validation runs:", error);
        setIsLoading(false);
      }
    );
  }, [apiKey, finetuneRunId, validationRunId]);

  useEffect(() => {
    if (!validationRunId && finetuneRunId) {
      fetchLatestValidationRun();
    } else {
      setResolvedValidationRunId(validationRunId);
    }
  }, [validationRunId, finetuneRunId, fetchLatestValidationRun]);

  const fetchData = useCallback(
    (showLoading: boolean = false) => {
      if (!resolvedValidationRunId) return;
      const loadingHandler = showLoading ? setIsLoading : null;
      ValidationService.getValidationRequests(
        resolvedValidationRunId,
        loadingHandler,
        apiKey
      ).subscribe(
        (data: ValidationRun) => {
          setValidationRun(data);
        },
        (error: any) => {
          console.error("Error fetching validation requests:", error);
        }
      );
    },
    [apiKey, resolvedValidationRunId]
  );

  useEffect(() => {
    if (resolvedValidationRunId) {
      fetchData(true);
    }
  }, [apiKey, resolvedValidationRunId]);

  useEffect(() => {
    if (validationRun?.status?.toUpperCase() === "RUNNING") {
      const pollingInterval = 15000;
      const intervalId = setInterval(() => {
        fetchData(false);
      }, pollingInterval);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [validationRun?.status]);

  // Add global CSS for interactive rows
  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
      .interactive-row:hover td {
        background-color: #f5f8ff !important;
      }
      .details-button:hover {
        background-color: #f0f7ff !important;
        box-shadow: 0 2px 4px rgba(24, 144, 255, 0.1) !important;
      }
      .ant-table-tbody > tr.ant-table-row:hover > td {
        background-color: #f5f8ff !important;
      }
      .ant-statistic-content-value {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleViewRequest = (request: ValidationRequest) => {
    setSelectedRequest(request);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedRequest(null);
  };

  if (isLoading) {
    return (
      <div
        className="loading-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 200px)",
        }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!resolvedValidationRunId) {
    return (
      <div
        className="no-data-container"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 200px)",
          flexDirection: "column",
        }}>
        <Empty
          description={
            <div>
              <Text strong style={{ display: "block", marginBottom: "8px" }}>
                No validation run found
              </Text>
              <Text type="secondary">
                {finetuneRunId
                  ? "No validation runs available for this finetune run."
                  : "Please provide a validation run ID or finetune run ID."}
              </Text>
            </div>
          }
        />
      </div>
    );
  }

  const requestColumns: TableColumnsType<any> = [
    {
      title: "Date Created",
      dataIndex: "date_created",
      key: "date_created",
      width: "15%",
      render: (timestamp: number) => {
        const date = moment(timestamp);
        return (
          <Tooltip title={date.format("MMMM D, YYYY h:mm:ss A")}>
            <div>
              <div style={{ fontWeight: 500 }}>
                {date.format("MMM D, YYYY")}
              </div>
              <div style={{ color: "#8c8c8c", fontSize: "12px" }}>
                {date.format("h:mm A")}
              </div>
            </div>
          </Tooltip>
        );
      },
      sorter: (a: any, b: any) => a.date_created - b.date_created,
    },
    {
      title: "Request ID",
      dataIndex: "request_id",
      key: "request_id",
      width: "15%",
      render: (text: string) => (
        <Text style={styles.monospaceFont}>{text}</Text>
      ),
    },
    {
      title: "Status",
      dataIndex: "correct",
      key: "correct",
      width: "10%",
      render: (correct: boolean) => (
        <Tag color={correct ? "success" : "error"} style={styles.tagStyle}>
          {correct ? "Correct" : "Incorrect"}
        </Tag>
      ),
      sorter: (a: any, b: any) => {
        if (a.correct === b.correct) return 0;
        return a.correct ? 1 : -1;
      },
    },
    {
      title: "Expected Output",
      dataIndex: "expected_output",
      key: "expected_output",
      width: "25%",
      render: (text: string) => (
        <div
          style={{
            whiteSpace: "pre-wrap",
            maxHeight: "150px",
            overflow: "auto",
          }}>
          {text}
        </div>
      ),
    },
    {
      title: "Actual Output",
      dataIndex: "actual_output",
      key: "actual_output",
      width: "25%",
      render: (text: string) => (
        <div
          style={{
            whiteSpace: "pre-wrap",
            maxHeight: "150px",
            overflow: "auto",
          }}>
          {text}
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "10%",
      align: "center" as const,
      render: (text: string, record: ValidationRequest) => (
        <RequestViewButton
          apiKey={apiKey}
          requestId={record.request_id}
          applicationId={record.original_application_id?.toString()}
          applicationActionId={record.original_intent_id?.toString()}
          onClick={() => handleViewRequest(record)}>
          Details
        </RequestViewButton>
      ),
    },
  ];

  return (
    <>
      <Row gutter={[24, 24]}>
        <Col span={24}>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={8}>
              <div style={styles.statsCard("#1890ff")}>
                <Space>
                  <FileTextOutlined
                    style={{ color: "#1890ff", fontSize: "24px" }}
                  />
                  <div>
                    <Text
                      type="secondary"
                      style={{ fontSize: "12px", display: "block" }}>
                      Total Requests
                    </Text>
                    <Text strong style={{ fontSize: "18px" }}>
                      {validationRun?.total_requests || 0}
                    </Text>
                  </div>
                </Space>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div style={styles.statsCard("#52c41a")}>
                <Space>
                  <CheckCircleOutlined
                    style={{ color: "#52c41a", fontSize: "24px" }}
                  />
                  <div>
                    <Text
                      type="secondary"
                      style={{ fontSize: "12px", display: "block" }}>
                      Accuracy
                    </Text>
                    <Text strong style={{ fontSize: "18px" }}>
                      {validationRun?.accuracy !== undefined
                        ? `${((validationRun?.accuracy || 0) * 100).toFixed(
                            2
                          )}%`
                        : "N/A"}
                    </Text>
                  </div>
                </Space>
              </div>
            </Col>
            <Col xs={24} sm={8}>
              <div style={styles.statsCard("#722ed1")}>
                <Space>
                  <RocketOutlined
                    style={{ color: "#722ed1", fontSize: "24px" }}
                  />
                  <div>
                    <Text
                      type="secondary"
                      style={{ fontSize: "12px", display: "block" }}>
                      Status
                    </Text>
                    <Tag
                      color={getStatusColor(validationRun?.status || "")}
                      style={styles.tagStyle}>
                      {validationRun?.status?.toUpperCase() || "N/A"}
                    </Tag>
                  </div>
                </Space>
              </div>
            </Col>
          </Row>
        </Col>
        <Row>
          <Col span={24}>
            <Row>
              <Col span={24}>
                <div style={styles.tableContainer}>
                  <Table
                    dataSource={validationRun.requests}
                    columns={requestColumns}
                    rowKey="request_id"
                    rowClassName={() => "interactive-row"}
                    pagination={{
                      defaultPageSize: 10,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "20", "50", "100"],
                    }}
                    locale={{
                      emptyText: (
                        <Empty
                          description={
                            <div>
                              <Text
                                strong
                                style={{
                                  display: "block",
                                  marginBottom: "8px",
                                }}>
                                No requests found
                              </Text>
                              <Text type="secondary">
                                No validation requests are available.
                              </Text>
                            </div>
                          }
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                        />
                      ),
                    }}
                  />
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Row>

      <RequestViewModal
        isVisible={isModalVisible}
        onClose={handleCloseModal}
        apiKey={apiKey}
        requestId={selectedRequest?.request_id}
        applicationId={selectedRequest?.original_application_id?.toString()}
        applicationActionId={selectedRequest?.original_intent_id?.toString()}
      />
    </>
  );
};

export default ValidationDetails;
